// One-time step after a Google/Microsoft sign-up (backlog AUTH-8,
// 2026-09-04): a password signup collects Business Name inline, but an
// OAuth signup has no equivalent form, so handle_new_user() creates the
// account with business_name null. App.tsx's access gate routes any
// signed-in account with no business_name here, before anything else in
// the app — see requireAccess()'s needsBusinessName check. Not reachable
// (or skippable) any other way: no back/skip action, by design.
// Design ported from Lovable (2026-09-04), adapted for this project's real
// react-router-dom routing (the reference used TanStack Router's
// createFileRoute/object-based navigate, which doesn't exist here) and its
// real updateAccount() save (Lovable's own brief flagged its save call as a
// stub integration point, correctly).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";

import { AuthLayout, authFieldClassName, authPrimaryClass } from "@/components/snapdraw/auth-layout";
import { FieldError } from "@/components/snapdraw/form";
import { updateAccount } from "@/lib/profile";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import "@/styles/tailwind-preview.css";

export function OnboardingBusinessPage({ onSaved }: { onSaved: () => void }) {
  const navigate = useNavigate();
  const t = useT();
  const [business, setBusiness] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!business.trim()) {
      setError(t("auth.error.required"));
      return;
    }

    setBusy(true);
    try {
      await updateAccount({ business_name: business.trim() });
      onSaved();
      navigate("/");
    } catch {
      setError(t("onboarding.error.save"));
      setBusy(false);
    }
  }

  return (
    <AuthLayout eyebrow={t("onboarding.eyebrow")}>
      <h3 className="mt-4 font-display text-3xl uppercase tracking-wide text-foreground lg:text-4xl">
        {t("onboarding.title")}
      </h3>
      <p className="mt-3 text-base text-muted-foreground">{t("onboarding.help")}</p>

      <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
        <div className="space-y-2">
          <label className="relative flex items-center">
            <Building2 className="pointer-events-none absolute left-4 h-4 w-4 text-arc" />
            <input
              type="text"
              value={business}
              onChange={(e) => {
                setBusiness(e.target.value);
                if (error) setError(null);
              }}
              placeholder={t("signup.businessPlaceholder")}
              autoComplete="organization"
              disabled={busy}
              className={cn(authFieldClassName(!!error), error && "pr-10")}
              aria-invalid={!!error}
              aria-describedby={error ? "onboarding-business-error" : undefined}
            />
          </label>
          {error && <FieldError id="onboarding-business-error">{error}</FieldError>}
        </div>

        <button type="submit" disabled={busy} className={authPrimaryClass}>
          {busy ? t("onboarding.submitting") : t("onboarding.submit")}
        </button>
      </form>
    </AuthLayout>
  );
}
